
"use client";

import { createContext, useContext, useEffect, useState, type ReactNode, useCallback } from 'react';
import { type OfferPartner, surveyPartners as fallbackSurveyPartners } from '@/lib/data';
import { db } from '@/lib/firebase';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, orderBy, writeBatch } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';

interface SurveyPartnerContextType {
  surveyPartners: OfferPartner[];
  addSurveyPartner: (partner: Omit<OfferPartner, 'id'>) => Promise<void>;
  updateSurveyPartner: (id: string, updatedPartner: Partial<Omit<OfferPartner, 'id'>>) => Promise<void>;
  deleteSurveyPartner: (id: string) => Promise<void>;
  loading: boolean;
  refreshSurveyPartners: () => void;
}

const SurveyPartnerContext = createContext<SurveyPartnerContextType | undefined>(undefined);

export const SurveyPartnerProvider = ({ children }: { children: ReactNode }) => {
  const [surveyPartners, setSurveyPartners] = useState<OfferPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSurveyPartners = useCallback(async () => {
    setLoading(true);
    try {
      const partnersCollection = collection(db, 'surveyPartners');
      const q = query(partnersCollection, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);

      const partnerList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
      } as OfferPartner));
      setSurveyPartners(partnerList);
      
    } catch (error) {
      console.error("Failed to fetch survey partners from Firestore", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not load survey partners from the database." });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSurveyPartners();
  }, [fetchSurveyPartners]);

  const addSurveyPartner = async (partnerData: Omit<OfferPartner, 'id'>) => {
    try {
      const partnersCollection = collection(db, 'surveyPartners');
      const docRef = await addDoc(partnersCollection, partnerData);
      setSurveyPartners(prev => [...prev, {...partnerData, id: docRef.id}].sort((a,b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Error adding survey partner:", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not add the survey partner." });
      throw error;
    }
  };

  const updateSurveyPartner = async (id: string, updatedPartnerData: Partial<Omit<OfferPartner, 'id'>>) => {
     try {
       if (Object.keys(updatedPartnerData).length === 0) {
           toast({ title: "No changes to save." });
           return;
       }

       const partnerDocRef = doc(db, 'surveyPartners', id);
       await updateDoc(partnerDocRef, updatedPartnerData);
       
       setSurveyPartners(prev =>
         prev.map(p => (p.id === id ? { ...p, ...updatedPartnerData } : p)).sort((a,b) => a.name.localeCompare(b.name))
       );
     } catch (error) {
       console.error("Error updating survey partner:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not update the survey partner." });
       throw error;
     }
  };

  const deleteSurveyPartner = async (id: string) => {
    try {
      const partnerDocRef = doc(db, 'surveyPartners', id);
      await deleteDoc(partnerDocRef);
      setSurveyPartners(prev => prev.filter(p => p.id !== id));
    } catch (error) {
       console.error("Error deleting survey partner:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not delete the survey partner." });
       throw error;
    }
  };

  return (
    <SurveyPartnerContext.Provider value={{ surveyPartners, addSurveyPartner, updateSurveyPartner, deleteSurveyPartner, loading, refreshSurveyPartners: fetchSurveyPartners }}>
      {children}
    </SurveyPartnerContext.Provider>
  );
};

export const useSurveyPartners = () => {
  const context = useContext(SurveyPartnerContext);
  if (context === undefined) {
    throw new Error('useSurveyPartners must be used within a SurveyPartnerProvider');
  }
  return context;
};
